import { toast } from '@/lib/sonner-adapter';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

/**
 * Parse Supabase/Edge Function errors into user-friendly messages
 */
function parseError(error: unknown): string {
  if (!error) return 'An unexpected error occurred';

  // Handle Error objects
  if (error instanceof Error) {
    const message = error.message;

    // Skip generic Supabase messages - don't show them
    if (message.includes('Edge Function returned a non-2xx status code')) {
      return ''; // Return empty to skip showing toast
    }
    if (message.includes('FunctionsHttpError')) {
      return ''; // Skip generic function errors
    }

    return message;
  }

  // Handle object with message/error properties
  if (typeof error === 'object' && error !== null) {
    const err = error as Record<string, unknown>;
    if (typeof err.message === 'string') return err.message;
    if (typeof err.error === 'string') return err.error;
  }

  return String(error);
}

/**
 * Shared QueryClient configuration for all admin hooks
 * 
 * - Conservative retry (1x) to avoid hammering failed endpoints
 * - 5s stale time for reasonable caching without staleness
 * - No refetch on window focus to avoid unnecessary requests
 * - Global error handling for mutations (shows toast on error)
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 5000,
      refetchOnWindowFocus: false,
      suspense: false, // CRITICAL: Prevent React error #300 globally
    },
    mutations: {
      retry: 0, // Don't retry failed mutations
    },
  },
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      // Skip if mutation has its own onError handler
      if (mutation.options.onError) return;

      const message = parseError(error);
      if (message) {
        toast.error(message);
      }
    },
  }),
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Only show toast for background refetch errors, not initial loads
      // Initial loads show error in UI component
      if (query.state.data !== undefined) {
        const message = parseError(error);
        if (message) {
          // Don't show generic errors for queries - they usually have retry
          console.warn('[QueryClient] Query error:', message);
        }
      }
    },
  }),
});
